import type { QueryKey } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { DONATION_ROUTES } from '@/constants';
import { BASE_URL } from '@/constants/env';

import {
  AD_NOTICE,
  formatMonthDay,
  formatWon,
  TARGET_TYPE_LABEL,
} from './labels';
import type {
  Channel,
  CreatePartnersLinkRequest,
  GiftProduct,
  InstitutionEvent,
  PartnersLink,
  PartnersLinkList,
  PartnersLinkPage,
  Region,
  TargetType,
} from './types';

export type SearchType = Exclude<TargetType, 'PAGE'>;

export interface LinkTarget {
  targetType: TargetType;
  targetKey?: string;
  targetName: string;
  destinationPath: string;
  description?: string;
  region?: string;
  price?: number;
  period?: string;
}

export interface LinkFormValue {
  choice: string;
  query: string;
  target: LinkTarget | null;
  channel: Channel | '';
  name: string;
}

export const pageTargetOf = (page: PartnersLinkPage): LinkTarget => ({
  targetType: 'PAGE',
  targetKey: page.partnersLinkPageId,
  targetName: page.pageName,
  destinationPath: page.pagePath,
  description: page.pageDescription,
});

export const giftTargetOf = ({
  productNumber,
  productName,
  institutionShortName,
  requiredDonationAmount,
}: GiftProduct): LinkTarget | null => {
  if (!productNumber || !productName) {
    return null;
  }

  return {
    targetType: 'GIFT_PRODUCT',
    targetKey: productNumber,
    targetName: productName,
    destinationPath: DONATION_ROUTES.GIFTS_DETAIL.pathname({
      productNumber: encodeURIComponent(productNumber),
    }),
    region: institutionShortName,
    price: requiredDonationAmount,
  };
};

export const regionTargetOf = ({
  fullName,
  institutionSlugId,
}: Region): LinkTarget => ({
  targetType: 'REGION',
  targetKey: institutionSlugId,
  targetName: fullName,
  destinationPath: DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
    id: encodeURIComponent(institutionSlugId),
  }),
});

export const eventTargetOf = ({
  eventId,
  title,
  endDate,
}: Pick<InstitutionEvent, 'eventId' | 'title' | 'endDate'>): LinkTarget => ({
  targetType: 'MONTHLY_EVENT',
  targetKey: String(eventId),
  targetName: title,
  destinationPath: DONATION_ROUTES.MY_ETC_EVENT_DETAIL.pathname({ eventId }),
  period: `~ ${dayjs(endDate).format('YYYY.MM.DD')}`,
});

const removeWhitespace = (text: string) => text.replace(/\s/g, '');

export const filterTargets = (targets: LinkTarget[], query: string) => {
  const keyword = removeWhitespace(query).toLowerCase();

  return targets.filter((target) =>
    removeWhitespace(target.targetName).toLowerCase().includes(keyword),
  );
};

export const targetDetailsOf = (target: LinkTarget) => {
  if (target.targetType === 'GIFT_PRODUCT') {
    return [
      target.region,
      target.price === undefined ? undefined : formatWon(target.price),
    ].filter((detail): detail is string => Boolean(detail));
  }

  if (target.targetType === 'PAGE' || target.targetType === 'REGION') {
    return target.description ? [target.description] : [];
  }

  return target.period ? [target.period] : [];
};

export const toCreateRequest = ({
  target,
  channel,
  name,
}: Pick<LinkFormValue, 'channel' | 'name'> & {
  target: LinkTarget;
}): CreatePartnersLinkRequest => ({
  targetType: target.targetType,
  ...(target.targetKey ? { targetKey: target.targetKey } : {}),
  targetName: target.targetName,
  destinationPath: target.destinationPath,
  ...(channel ? { channel } : {}),
  contentName: name.trim(),
});

export const targetViewUrlOf = (target: Pick<LinkTarget, 'destinationPath'>) =>
  `${BASE_URL}${target.destinationPath}`;

export const shortLinkUrlOf = (code: string) => `${BASE_URL}/r/${code}`;

export const shortLinkOf = (code: string) =>
  shortLinkUrlOf(code).replace(/^https?:\/\//, '');

export const linkCopyTextOf = (code: string) =>
  `${AD_NOTICE}\n${shortLinkUrlOf(code)}`;

export const targetTextOf = ({
  targetType,
  targetName,
}: {
  targetType: TargetType;
  targetName: string;
}) =>
  targetType === 'PAGE'
    ? targetName
    : `${TARGET_TYPE_LABEL[targetType]} · ${targetName}`;

export const withCreatedLink = (
  list: PartnersLinkList,
  link: PartnersLink,
): PartnersLinkList => ({
  ...list,
  contents: [
    {
      ...link,
      clickCount: 0,
      donationCount: 0,
      donorCount: 0,
      rewardAmount: 0,
    },
    ...list.contents,
  ],
});

const queryOf = (queryKey: QueryKey) => {
  const [descriptor] = queryKey;

  if (
    typeof descriptor === 'object' &&
    descriptor !== null &&
    'query' in descriptor &&
    typeof descriptor.query === 'object' &&
    descriptor.query !== null
  ) {
    return descriptor.query as Record<string, unknown>;
  }

  return {};
};

//* 목록 API의 기간(from·to)은 실적 집계에만 쓰여 기간 밖 목록에도 새 링크가 나온다 — 채널만 거른다
export const isLinkListAffectedBy = (queryKey: QueryKey, channel: Channel) => {
  const query = queryOf(queryKey);

  return !(
    'channel' in query &&
    query.channel !== undefined &&
    query.channel !== channel
  );
};

export const createdLabelOf = ({ createAt }: { createAt: string }) =>
  `${formatMonthDay(createAt)} 생성`;
